import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Images } from "../../../utils/Images";

const VerificationCode = ({
  open,
  onClose,
  phoneNumber,
  email,
  onVerify,
  onResend,
  onSuccess,
  isLoading = false,
  error = null,
  showSuccessScreen = true,
}) => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [isSuccess, setIsSuccess] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const inputsRef = useRef([]);
  
  // Use email if provided, otherwise use phoneNumber
  const displayValue = email || phoneNumber || "";

  // Media queries for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <600px
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const isBelow900 = useMediaQuery("(max-width: 899px)");
  const isWidthSet = useMediaQuery("(min-width: 360px)");
  useEffect(() => {
    if (!open) return;
    setCode(["", "", "", "", "", ""]);
    setTimer(30);
    setIsSuccess(false);
    setVerificationError(null);
    setIsResending(false);
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [open]);

  // Update error state when error prop changes
  useEffect(() => {
    setVerificationError(error);
  }, [error]);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    if (val.length > 1) return;
    const updated = [...code];
    updated[idx] = val;
    setCode(updated);
    if (val && idx < 5) {
      inputsRef.current[idx + 1].focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1].focus();
    }
    
    // Show success screen when Enter is pressed
    if (e.key === "Enter") {
      handleVerify();
    }
  };

  const handleVerify = async () => {
    const otpCode = code.join("");
    if (otpCode.length !== 6) return;
    
    setVerificationError(null);
    try {
      // Call the onVerify callback which will handle API verification
      if (onVerify) {
        await onVerify(otpCode);
        // If onVerify succeeds (doesn't throw), optionally show success screen
        if (showSuccessScreen) {
          setIsSuccess(true);
        }
      } else if (showSuccessScreen) {
        // Fallback if no onVerify provided and success screen is enabled
        setIsSuccess(true);
      }
    } catch (error) {
      // Error is handled by onVerify, just reset code
      setCode(["", "", "", "", "", ""]);
      if (inputsRef.current[0]) {
        inputsRef.current[0].focus();
      }
    }
  };

  const handleSuccessClose = () => {
    setIsSuccess(false);
    // When user closes success popup, allow parent to react (e.g., navigate home)
    if (onSuccess) {
      onSuccess();
    }
    onClose && onClose();
  };

  const handleResendClick = async () => {
    if (onResend && timer === 0 && !isResending) {
      try {
        setIsResending(true);
        await onResend();
        // Reset timer after successful resend
        setTimer(30);
      } catch (error) {
        // Error is handled by parent component via error prop
        console.error("Error resending OTP:", error);
      } finally {
        setIsResending(false);
      }
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "rgba(0,0,0,0.3)",
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",      
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "20px",
          padding: isMobile ? "15px" : isAbove1200 ? "30px" : "20px",
          minWidth: isMobile ? "320px" : isAbove1200 ? "500px" : "500px",
          maxWidth: isMobile ? "320px" : isAbove1200 ? "500px" : "460px",
          boxShadow: 24,
          position: "relative",
          width: "100%",
        }}
      >
        <IconButton
          onClick={isSuccess ? handleSuccessClose : onClose}
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
        
        {!isSuccess ? (
          <>
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{
                color: "#0A0A23",
                marginBottom: "18px",
                fontSize: isMobile ? 16 : isAbove1200 ? 22 : 18,
              }}
            >
              Verification Code
            </Typography>

        <Box
          mb={2}
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            color: "#0A0A23",
            marginBottom: "18px",
            fontSize: isMobile ? 12 : isAbove1200 ? 16 : 15,
            fontWeight: 500,
          }}
        >
          6 Digit Verification Code Sent To
          <Box
            component="span"
            sx={{ display: "flex", alignItems: "center", ml: 1 }}
          >
            <b style={{ fontWeight: 500, fontSize: isMobile? "10px" :isAbove1200? 14:12 }}>{displayValue}</b>
            <IconButton size="small" sx={{ p: 0.5, ml: 1 }} onClick={onClose}>
              <img
                src={Images.edit}
                alt="Edit"
                style={{ width: 20, height: 20, display: "block" }}
              />
            </IconButton>
          </Box>
        </Box>
        
        {/* Error Message */}
        {verificationError && (
          <Typography
            sx={{
              color: "#F04951",
              fontSize: isMobile ? 12 : 14,
              mb: 2,
              textAlign: "center",
            }}
          >
            {verificationError}
          </Typography>
        )}

        <Box display="flex" gap={"8px"} justifyContent="start" mb={2}>
          {code.map((digit, idx) => (
            <TextField
              key={idx}
              inputRef={(el) => (inputsRef.current[idx] = el)}
              value={digit}
              onChange={(e) => handleChange(e, idx)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: 16,
                  width: isMobile ? "26px" : isAbove1200 ? "43px" : "36px",
                  borderRadius: 4,
                  height: isMobile ? "26px" : isAbove1200 ? "43px" : "36px",
                  padding: 0,
                },
              }}
              //  sx={{ width: 43 }}
            />
          ))}
        </Box>
        <Typography
          textAlign="right"
          mb={2}
          sx={{
            color: "#141414",
            fontWeight: 500,
            marginBottom: "18px",
            fontSize: "14px",
          }}
        >
          {timer > 0 ? (
            <>
              Didn't receive the code?{" "}
              <Box
                component="span"
                sx={{
                  color: "#5E6366",
                  fontWeight: 500,
                }}
              >
                {`00:${timer.toString().padStart(2, "0")}`}
              </Box>
            </>
          ) : (
            <>
              Didn't receive the code?{" "}
              <Button
                variant="text"
                disabled={isResending}
                onClick={handleResendClick}
                sx={{
                  textTransform: "none",
                  color: "#0072BC",
                  fontWeight: 500,
                  "&.Mui-disabled": {
                    color: "#0072BC",
                    opacity: 1,
                  },
                }}
              >
                {isResending ? "Sending..." : "Re-send"}
              </Button>
            </>
          )}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={handleVerify}
          disabled={code.some((d) => !d) || isLoading}
          sx={{
            bgcolor: "#F04951",
            color: "#fff",
            borderRadius: 2,
            fontSize: isMobile ? 10 : 14,
            py: 1.5,
            mt: 0,
            textTransform: "none",
            fontWeight: 500,
            letterSpacing: 0.5,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#E03E46",
              boxShadow: "none",
            },
            "&:active": {
              bgcolor: "#D63640",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              bgcolor: "#F04951",
              color: "#fff",
            },
          }}
        >
          {isLoading ? "Verifying..." : "Verify"}
        </Button>
          </>
        ) : (
          // Success Screen
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              py: isMobile ? 2 : 3,
            }}
          >
            {/* Success Icon */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: isMobile ? 60 : 80,
                  height: isMobile ? 60 : 80,
                  bgcolor: "#E8F5E8",
                  borderRadius: "50%",
                }}
              >
                <Box
                  component="img"
                  src={Images.successIcon}
                  alt="Success"
                  sx={{ 
                    width: isMobile ? 30 : 40, 
                    height: isMobile ? 30 : 40 
                  }}
                />
              </Box>
            </Box>

            {/* Success Message */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? 18 : isAbove1200 ? 24 : 20,
                color: "#1D1D1B",
                marginBottom: 2,
                maxWidth: "340px",
              }}
            >
              Verification Successful!
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontWeight: 400,
                fontSize: isMobile ? 14 : 16,
                color: "#686866",
                marginBottom: 3,
                maxWidth: "400px",
                lineHeight: 1.5,
              }}
            >
              {email 
                ? "Your email has been verified successfully. Your campaign request has been submitted."
                : "Your phone number has been verified successfully. You can now proceed with your request."}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VerificationCode;
